import { ChangeEvent } from 'react';
import { FormLabel, InputLabel, MenuItem, Select } from '@mui/material';
import { brightpathTheme } from '@youscience/theme';
import {
  CoreBox,
  CoreCheckbox,
  CoreFormControl,
  CoreFormControlLabel,
  CoreFormGroup,
  CoreTextField,
  CoreTypography,
} from '@youscience/core';
import {
  GradeLevel,
  SubjectAreaMapping,
  TeacherEvaluation,
  TeacherEvaluationSchema,
} from '@youscience/commonapp-service-common';

import { FormState } from '@constants/commonApp/types';

import SectionAccordion from '@components/SectionAccordion';
import ContinueButton from '@components/ContinueButton';
import RichTextEditor from '@components/RichTextEditor';

import { StyledFieldsContainer, StyledFieldWrapper } from '@pages/StudentDetailsPage/components/Card.styles';
import FileUpload from '@pages/StudentDetailsPage/components/common/FileUpload';
import { getFilename, handleZodValidation } from '@pages/StudentDetailsPage/components/common/formUtils';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type WrittenEvaluationFields = Partial<TeacherEvaluation['writtenEvaluation']>;

const MAX_WORDS = 1000;

interface WrittenEvaluationProps {
  writtenEvaluation?: WrittenEvaluationFields;
  onFormChange: (formState: FormState<WrittenEvaluationFields>) => void;
  onExpand: () => void;
  onSaveAndContinue?: () => void;
  isExtended: boolean;
  subjectArea?: string;
  isSaved: boolean;
  applicantName?: string;
}

const validator = TeacherEvaluationSchema.shape.writtenEvaluation;

export const WrittenEvaluation = ({
  writtenEvaluation = {},
  onFormChange,
  onExpand,
  onSaveAndContinue,
  isExtended,
  subjectArea,
  isSaved,
  applicantName = '',
}: WrittenEvaluationProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<WrittenEvaluationFields, typeof validator>(
    writtenEvaluation,
    validator,
    isSaved,
    onFormChange,
  );

  const handleFileChange = (field: string) => async (file: string | undefined) => {
    setFormStateWithErrors({ [field]: file });
  };

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value && handleZodValidation(name, validator, { [name]: value }).isError) return;
    setFormStateWithErrors({ [name]: value || undefined });
  };

  const handleRichTextEditorChange = (name: string) => (content: string) => {
    setFormStateWithErrors({ [name]: content });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as (typeof GradeLevel)[keyof typeof GradeLevel];

    const updatedGradeLevels: (typeof GradeLevel)[keyof typeof GradeLevel][] = event.target.checked
      ? [...(formState?.data?.gradeLevels?.filter((gradeLevel) => gradeLevel !== value) || []), value]
      : formState?.data?.gradeLevels?.filter((gradeLevel) => gradeLevel !== value) || [];

    const isOtherSelected = updatedGradeLevels.includes(GradeLevel.Other);

    setFormStateWithErrors({
      gradeLevels: updatedGradeLevels,
      otherLevels: isOtherSelected ? formState.data.otherLevels : undefined,
    });
  };

  const sortedSubjectAreas = Object.entries(SubjectAreaMapping).sort((a, b) => {
    const aValue = a[1];
    const bValue = b[1];

    if (aValue === 'Other') return 1;
    if (bValue === 'Other') return -1;

    return aValue.localeCompare(bValue);
  });

  const subjectOptions = sortedSubjectAreas.map(([key, value]) => (
    <MenuItem key={key} value={key}>
      {value}
    </MenuItem>
  ));

  return (
    <SectionAccordion
      title="Written evaluation"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
    >
      <StyledFieldsContainer>
        <CoreFormControl fullWidth>
          <InputLabel>Subject</InputLabel>

          <Select data-testid="subject" label="Subject" value={subjectArea ?? ''} disabled>
            <MenuItem key="" value="" />

            {subjectOptions}
          </Select>
        </CoreFormControl>

        <CoreTypography>
          You can submit this form only one time for this applicant. Your responses and all accompanying documents will
          be shared with all of the colleges to which the applicant applies. For that reason, you should not tailor this
          form for any particular institution. Once you submit, you will be unable to modify these forms or documents in
          any way.
        </CoreTypography>

        <RichTextEditor
          title="How long have you known this student and in what context?"
          value={formState.data.context || ''}
          wordsLimit={MAX_WORDS}
          onChange={handleRichTextEditorChange('context')}
        />

        <RichTextEditor
          title="What are the first words that come to your mind to describe this student?"
          value={formState.data.firstWords || ''}
          wordsLimit={MAX_WORDS}
          onChange={handleRichTextEditorChange('firstWords')}
        />

        <StyledFieldWrapper>
          <CoreTypography variant="subtitle2" component="h4">
            In which grade level(s) was student enrolled when you taught them?
          </CoreTypography>

          <CoreFormControl>
            <FormLabel>Select your options</FormLabel>

            <CoreFormGroup sx={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              {Object.keys(GradeLevel).map((key) => (
                <CoreBox
                  key={key}
                  sx={{
                    display: 'grid',
                    gap: '.5rem 1.5rem',
                    gridTemplateColumns: '1fr 1fr',
                  }}
                >
                  <CoreBox display="flex">
                    <CoreFormControlLabel
                      label={<CoreTypography>{key}</CoreTypography>}
                      name={key}
                      sx={{ gridColumn: '1 / span 2' }}
                      control={
                        <CoreCheckbox
                          data-testid={`gradeLevel${key}`}
                          value={GradeLevel[key as keyof typeof GradeLevel]}
                          onChange={handleCheckboxChange}
                          checked={formState?.data?.gradeLevels?.includes(GradeLevel[key as keyof typeof GradeLevel])}
                        />
                      }
                    />

                    {formState?.data?.gradeLevels?.includes(GradeLevel.Other) && key === 'Other' ? (
                      <CoreTextField
                        inputProps={{ 'data-testid': 'otherLevels' }}
                        name="otherLevels"
                        value={formState.data.otherLevels || ''}
                        onChange={handleTextFieldChange}
                        error={!!formState.errors.otherLevels}
                        helperText={formState.errors.otherLevels}
                      />
                    ) : null}
                  </CoreBox>
                </CoreBox>
              ))}
            </CoreFormGroup>
            {formState.errors.gradeLevels && <CoreTypography color="error">This is field is required</CoreTypography>}
          </CoreFormControl>
        </StyledFieldWrapper>

        <RichTextEditor
          title="List the courses in which you have taught this student, including the level of course difficulty (AP, IB,
            accelerated, Cambridge, honors, elective; 100-level, 200-level, etc.)."
          value={formState.data.courses || ''}
          wordsLimit={MAX_WORDS}
          onChange={handleRichTextEditorChange('courses')}
        />

        <StyledFieldWrapper>
          <CoreTypography>
            Please upload a document describing what you think is important about this student, including a description
            of academic and personal characteristics, as demonstrated in your classroom We welcome information that will
            help us to differentiate this student
          </CoreTypography>

          <CoreBox
            sx={{ display: 'flex', gap: '.5rem', alignItems: 'center', color: brightpathTheme.palette.secondary.main }}
          >
            <FileUpload
              title="Upload short evaluation"
              name="shortEvaluation"
              initialFile={formState.data.shortEvaluation}
              previewTitle="Short evaluation"
              filename={formState.data.shortEvaluation ? getFilename(applicantName, 'short_evaluation') : undefined}
              onFileUpload={handleFileChange}
              onFileDelete={handleFileChange}
              error={formState.errors.shortEvaluation}
            />
          </CoreBox>
        </StyledFieldWrapper>
      </StyledFieldsContainer>

      {onSaveAndContinue && <ContinueButton onContinue={onSaveAndContinue} />}
    </SectionAccordion>
  );
};
